/* eslint-disable max-lines */
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { FC, useEffect, useMemo, useState } from "react";
import { FieldValues, SubmitHandler, useFormContext } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import Button from "../../../components/ui/Button";
import { SelectOption } from "../../../components/ui/input/Input.types";
import SelectInput from "../../../components/ui/input/SelectInput";
import useFetch from "../../../hooks/useFetch";
import { getAllLines } from "../../../services/Lines.service";
import { getTripsByLine } from "../../../services/Trips.service";
import { tripTimesLabel } from "../../../utils/tripDuration";

interface SearchTripProps {
  onSubmit: SubmitHandler<FieldValues>;
}

interface LineItem {
  id: number;
  lineName: string;
}

interface TripItem {
  trip: {
    id: number;
    departureTimeString: string;
    arrivalTimeString: string;
    defaultCurrencyId: number;
    // The whole trip length in minutes, 0 when no length is known.
    durationMinutes: number;
  };
}
const AGE_GROUP_OPTIONS = [{ name: "I/e rritur", id: 1 }];
const CURRENCY_OPTIONS = [
  { name: "Euro", id: 1 },
  { name: "Lekë", id: 2 }
];

const SearchTrip: FC<SearchTripProps> = ({ onSubmit }) => {
  const { register, handleSubmit, setValue, getValues } = useFormContext();
  const [searchParams] = useSearchParams();
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const { response: linesResponse } = useFetch(getAllLines, {
    initRequest: true,
    params: { pageindex: 1, pageSize: 999999 }
  });

  const { response: tripsResponse, handleFunc: getTrips } = useFetch(
    getTripsByLine,
    { initRequest: false }
  );

  const getParamAsNumber = (paramName: string): number | undefined => {
    return searchParams.has(paramName)
      ? Number(searchParams.get(paramName))
      : undefined;
  };

  useEffect(() => {
    const lineId = getParamAsNumber("lineId");
    if (lineId && searchParams.has("lineName")) {
      void getTrips(lineId);
    }
  }, [searchParams.get("lineId")]);

  useEffect(() => {
    setValue("ageGroupId", 1, { shouldValidate: true });
  }, [setValue]);

  useEffect(() => {
    if (searchParams.has("lineId") && searchParams.has("tripId")) {
      const lineId = getParamAsNumber("lineId");
      const tripId = getParamAsNumber("tripId");
      const defaultCurrencyId = getParamAsNumber("defaultCurrencyId");
      if (lineId) setValue("lineId", lineId, { shouldValidate: true });
      if (tripId) setValue("tripId", tripId, { shouldValidate: true });
      if (defaultCurrencyId) {
        setValue("defaultCurrencyId", defaultCurrencyId, {
          shouldValidate: true
        });
      }
      setValue("isTwoWayTrip", false, { shouldValidate: true });
    }
  }, [searchParams, setValue]);

  const lineOptions = useMemo(() => {
    const options =
      linesResponse?.data?.result?.map((el: LineItem) => ({
        name: el.lineName,
        id: el.id
      })) || [];

    if (
      searchParams.has("lineName") &&
      searchParams.has("lineId") &&
      options.length === 0
    ) {
      options.push({
        name: searchParams.get("lineName") || "",
        id: getParamAsNumber("lineId") || 0
      });
    }

    return options;
  }, [linesResponse, searchParams]);

  const tripOptions = useMemo(() => {
    const trips: TripItem[] = tripsResponse?.data ?? [];
    const options = trips.map((el) => ({
      name: tripTimesLabel(
        el.trip.departureTimeString,
        el.trip.arrivalTimeString,
        el.trip.durationMinutes
      ),
      id: el.trip.id
    }));

    if (
      searchParams.has("tripTime") &&
      searchParams.has("tripId") &&
      options.length === 0
    ) {
      options.push({
        name: searchParams.get("tripTime") || "",
        id: getParamAsNumber("tripId") || 0
      });
    }

    return options;
  }, [tripsResponse, searchParams]);

  const tripCurrencyById = useMemo(() => {
    const map = new Map<number, number>();
    tripsResponse?.data?.forEach((el: TripItem) => {
      map.set(el.trip.id, el.trip.defaultCurrencyId);
    });
    return map;
  }, [tripsResponse]);

  useEffect(() => {
    if (!tripsResponse?.data?.length) return;
    const selectedTripId =
      Number(getValues("tripId")) || getParamAsNumber("tripId");
    if (!selectedTripId) return;

    const matchedTrip = tripsResponse.data.find(
      (el: TripItem) => el.trip.id === selectedTripId
    );
    const nextCurrencyId = matchedTrip?.trip.defaultCurrencyId;
    const currentCurrencyId = Number(getValues("defaultCurrencyId"));
    if (nextCurrencyId && nextCurrencyId !== currentCurrencyId) {
      setValue("defaultCurrencyId", nextCurrencyId, { shouldValidate: true });
    }
  }, [tripsResponse, getValues, setValue, searchParams]);

  const toggleDropdown = (dropdown: string) => {
    setOpenDropdown((current) => (current === dropdown ? null : dropdown));
  };

  const handleOptionSelect = (
    formField: string,
    option: SelectOption,
    callback?: () => void
  ) => {
    setValue(formField, Number(option.id), { shouldValidate: true });
    setOpenDropdown(null);
    callback?.();
  };

  return (
    <div className="border-b md:-mx-7 mb-6 py-6 md:p-0">
      <h6 className="mb-6 text-xl md:px-7">Cakto çmimet për linjë</h6>
      <form onSubmit={handleSubmit(onSubmit)} className="md:px-7">
        <div className="md:flex items-center mb-6 w-full">
          <div className="w-full md:flex">
            <div className="flex md:w-full mr-0 md:mr-6 mt-6 md:mt-0 gap-6">
              <div className="flex-1">
                <SelectInput
                  label="Zgjedh linjën"
                  placeholder="Zgjedh"
                  options={lineOptions}
                  selectedId={getParamAsNumber("lineId")}
                  isOpen={openDropdown === "line"}
                  onClick={() => toggleDropdown("line")}
                  onOptionSelect={(option) => {
                    handleOptionSelect("lineId", option, () =>
                      getTrips(Number(option.id))
                    );
                  }}
                  {...register("lineId")}
                />
              </div>
              <div className="flex-1">
                <SelectInput
                  label="Zgjedh orarin"
                  placeholder="Zgjedh"
                  options={tripOptions}
                  selectedId={
                    Number(getValues("tripId")) || getParamAsNumber("tripId")
                  }
                  isOpen={openDropdown === "trip"}
                  onClick={() => toggleDropdown("trip")}
                  onOptionSelect={(option) => {
                    handleOptionSelect("tripId", option);
                    const currencyId = tripCurrencyById.get(Number(option.id));
                    if (currencyId) {
                      setValue("defaultCurrencyId", currencyId, {
                        shouldValidate: true
                      });
                    }
                  }}
                  {...register("tripId")}
                />
              </div>
            </div>

            <div className="flex md:w-1/3 mr-0 md:mr-6 mt-6 md:mt-0 gap-6">
              <div className="flex-1">
                <SelectInput
                  label="Valuta"
                  placeholder="Zgjedh"
                  options={CURRENCY_OPTIONS}
                  selectedId={
                    Number(getValues("defaultCurrencyId")) ||
                    getParamAsNumber("defaultCurrencyId")
                  }
                  isOpen={openDropdown === "currency"}
                  onClick={() => toggleDropdown("currency")}
                  onOptionSelect={(option) =>
                    handleOptionSelect("defaultCurrencyId", option)
                  }
                  {...register("defaultCurrencyId")}
                />
              </div>
              <div className="flex-1">
                <SelectInput
                  label="Lloji i biletës"
                  placeholder="Zgjedh"
                  options={AGE_GROUP_OPTIONS}
                  selectedId={1}
                  isOpen={openDropdown === "ageGroup"}
                  onClick={() => toggleDropdown("ageGroup")}
                  onOptionSelect={(option) =>
                    handleOptionSelect("ageGroupId", option)
                  }
                  {...register("ageGroupId")}
                />
              </div>
            </div>
          </div>
          <div className="md:flex h-[67px] items-end">
            <div className="">
              <Button
                type="submit"
                className="w-full h-11 flex justify-center items-center mt-4 md:mt-0"
              >
                <MagnifyingGlassIcon className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SearchTrip;
